import Tippy from "@tippyjs/react"
import LogoMenu from "assets/images/logo-menu.svg"
import { Avatar } from "components/Avatar"
import { TippyCustomzie, useTippyLayout } from "components/TippyCustomzie"
import {
  REACT_APP_API_MENU_WEB,
  REACT_APP_API_VERSION,
  RELEASE_NOTE_URL,
} from "config/environments"
import { PATH } from "constants/path"
import { pushTo } from "helpers/history"
import { useBoolean, useNumber, useString, useWindowSize } from "helpers/hooks"
import { setUserInfoAction } from "pages/auth/stores/actions"
import { emptyUserInfo, UserInfo } from "pages/auth/types"
import ActionItem from "pages/projects/molecules/ActionItem"
import { useLocation } from "react-router-dom"
import { configureStore, RootState } from "stores/configureStore"
import {
  MENU_LEFT,
  MENU_LEFT_CONVERSATIONS,
  MENU_LEFT_MY_ACCESS,
  MENU_LEFT_USER_GROUPS,
} from "./layput.constants"
import LogoutCard from "./organisms/LogoutCard"
import ModalSearch from "./organisms/ModalSearch"
import { ACTION_LOGOUT, MenuLeftProps } from "./types"

import { ReactComponent as CloseIcon } from "assets/images/icons/icon-close-24.svg"
import { ReactComponent as MenuBar } from "assets/images/icons/icon-editor-align-text-justify.svg"
import { ReactComponent as TraceliumLogo } from "assets/images/logo-tracelium-terms-policy.svg"
import Typography from "components/Typography"
import { NAME_COOKIE } from "constants/cookie"
import { useCookies } from "react-cookie"
import {
  UnreadBuild,
  UnreadConversation,
} from "pages/conversations/conversations.type"
import { Clock } from "components/Clock"
import { useSelector } from "react-redux"
import { postSwitchOrganizationMiddleware } from "pages/auth/services/api"
import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { useEffect, useState } from "react"
import { endLoading, startLoading } from "reducers/loading"
import { ReactComponent as AddButton } from "assets/images/icons/add-button.svg"
import { OrganizationIconCard } from "components/OrganizationIconCard"
import { PERMISSIONS } from "constants/permissions"
import InputDefault from "components/Input/InputDefault"
import { ReactComponent as IconSearchNormal } from "assets/images/icons/icon-search-normal.svg"
import {
  getSavedWsCode,
  getWsCodeFromUrl,
  saveWsCode,
} from "helpers/wsCode.helper"

interface Props {
  userInfo: UserInfo
  unreadBuild: UnreadBuild
  unreadConversation: UnreadConversation
}
const UnreadIcon = (props: { totalUnread: number }) => {
  return props.totalUnread !== 0 ? (
    <div
      className=" flex items-center justify-center w-6 h-5 min-h-5 mr-[30px] md:w-5 md:h-4 md:absolute md:right-[14px] md:top-[2px] md:m-0 text-white z-10"
      style={{ background: "#EA4545", borderRadius: "999px" }}
    >
      <p
        className="font-body font-semibold px-1 py-[1px]"
        style={{ fontSize: "10px" }}
      >
        {props.totalUnread}
      </p>
    </div>
  ) : null
}
const UnreadConversationIcon = (props: { totalUnread: number }) => {
  return props.totalUnread !== 0 ? (
    <div
      className=" flex items-center justify-center w-6 h-5 min-h-5 mr-[30px] md:w-5 md:h-4 md:absolute md:right-[-10px] md:top-[2px] md:m-0 text-white z-10"
      style={{ background: "#EA4545", borderRadius: "999px" }}
    >
      <p
        className="font-body font-semibold px-1 py-[1px]"
        style={{ fontSize: "10px" }}
      >
        {props.totalUnread}
      </p>
    </div>
  ) : null
}
const MenuLeft = (props: Props) => {
  const { userInfo, unreadBuild, unreadConversation } = props
  const { fontEndVersion } = useSelector(
    (state: RootState) => state.versionDeploy
  )
  const [_cookies, _setCookie, removeCookie] = useCookies([
    NAME_COOKIE.PROJECT,
    NAME_COOKIE.PROJECT_COMPONENT,
    NAME_COOKIE.CONVERSATIONS,
  ])
  const [organizations, setOrganizations] = useState<
    {
      id: string
      name: string
      status: string
      is_blocked: number
      background_color: string
      logo_url: string
      code: string
    }[]
  >([])
  const organizationSelected = useString("")
  const organizationSearch = useString("")

  const { isMobile } = useWindowSize()
  const { TippyLayout } = useTippyLayout()

  const location = useLocation() as any

  const pathnameLocation =
    location.pathname === "/" ? PATH.projects : location.pathname

  const showMenuTippy = useBoolean()
  const openSelectOrganization = useBoolean()
  const showLogout = useBoolean()
  const showMenuBar = useBoolean()
  const menuEnv = REACT_APP_API_MENU_WEB
    ? JSON.parse(REACT_APP_API_MENU_WEB)
    : []
  const unreadLogCount = useNumber(0)
  const [menuLeft, setMenuLeft] = useState<MenuLeftProps[]>([])

  useEffect(() => {
    if (userInfo?.organizations?.length && userInfo?.id) {
      setOrganizations(userInfo.organizations.filter((el) => el.is_access))
      const wsCode = getSavedWsCode()
      if (wsCode) {
        const organization = userInfo?.organizations?.find(
          (el) => el.db_name === wsCode.toLowerCase()
        )
        if (organization) {
          organizationSelected.setValue(organization.id)
        } else if (userInfo?.last_organization_id) {
          organizationSelected.setValue(userInfo.last_organization_id)
        }
      }
    }
  }, [
    JSON.stringify(userInfo?.organizations),
    userInfo?.id,
    userInfo?.user_cognito_id,
    userInfo?.last_organization_id,
  ])

  useEffect(() => {
    if (userInfo?.unreadLogCount) {
      unreadLogCount.setValue(userInfo.unreadLogCount)
    }
  }, [userInfo?.unreadLogCount])

  useEffect(() => {
    if (!userInfo.last_organization_id || !userInfo?.id) {
      setMenuLeft([])
      return
    }
    const permissions = userInfo?.permissions
    const newMenuLeft: MenuLeftProps[] = []
    if (permissions.includes(PERMISSIONS.PROJECT_VIEW)) {
      newMenuLeft.push(MENU_LEFT[0])
    }
    if (permissions.includes(PERMISSIONS.PROJECT_BUILD_VIEW)) {
      newMenuLeft.push(MENU_LEFT[1])
    }
    if (
      permissions.includes(PERMISSIONS.GROUP_ORGANIZATION_VIEW) &&
      permissions.includes(PERMISSIONS.USER_VIEW)
    ) {
      newMenuLeft.push(MENU_LEFT_USER_GROUPS)
    } else {
      newMenuLeft.push(MENU_LEFT_MY_ACCESS)
    }

    newMenuLeft.push(MENU_LEFT[3])
    setMenuLeft(newMenuLeft)
  }, [userInfo?.id, JSON.stringify(userInfo?.permissions)])

  const handleMenuPage = (newPath: string) => () => {
    if (newPath === PATH.assignment) {
      if (location.pathname.indexOf("/assign-to") === 0) return
      pushTo(`/assign-to/${userInfo?.id}`)
      return
    }
    if (newPath === "/setting") {
      pushTo("/generals", undefined, { tab: 3 })
    } else {
      if (location.pathname.indexOf(newPath) === 0) return
      pushTo(newPath)
    }
  }
  const handleClickTippy = (event) => {
    event.stopPropagation()
    event.preventDefault()
    showMenuTippy.setValue(!showMenuTippy.value)
  }

  const onClickAction = (key: "logout" | "myProfile") => () => {
    if (key === "myProfile") {
      pushTo(PATH.general, undefined, { tab: 0 })
      return
    }
    showLogout.setValue(true)
    showMenuTippy.setValue(false)
  }
  const handleChangeButton = (key: ACTION_LOGOUT) => () => {
    if (key === ACTION_LOGOUT.NO) {
      showLogout.setValue(false)
      return
    }

    // Clear all localStorage
    localStorage.clear()

    /// clear project, component, build and conversation filter
    removeCookie(NAME_COOKIE.PROJECT)
    removeCookie(NAME_COOKIE.PROJECT_COMPONENT)
    removeCookie(NAME_COOKIE.CONVERSATIONS)

    configureStore.dispatch(setUserInfoAction(emptyUserInfo))
    pushTo(PATH.login)
  }

  const handleSwitchOrganization =
    (
      newOrganizationId: string,
      newOrganizationStatus: string,
      newOrganizationIsBlocked: number,
      newOrganizationCode: string
    ) =>
    async () => {
      if (
        Boolean(newOrganizationIsBlocked) ||
        newOrganizationStatus === "INACTIVE"
      ) {
        toast(
          <LabelNotificationPage
            messenger="This workspace is blocked. Please try again later!"
            type="error"
          />
        )
        return
      }
      if (newOrganizationId === organizationSelected.value) {
        openSelectOrganization.setValue(false)
        return
      }
      if (newOrganizationStatus === "PROCESS") {
        toast(
          <LabelNotificationPage
            messenger="This workspace is still being processed. Please try again later!"
            type="warning"
          />
        )
        return
      }
      configureStore.dispatch(startLoading())
      try {
        const data = await postSwitchOrganizationMiddleware({
          organization_id: newOrganizationId,
        })
        if (data.token) {
          localStorage.setItem("access_token", data.token)
          saveWsCode(newOrganizationCode, data.token)
          configureStore.dispatch(endLoading())
          window.location.href = `/${newOrganizationCode}${PATH.projects}`
          return
        }

        toast(
          <LabelNotificationPage
            messenger={`Switch workspace failed`}
            type="error"
          />
        )
        configureStore.dispatch(endLoading())
      } catch (error: any) {
        configureStore.dispatch(endLoading())
        toast(
          <LabelNotificationPage
            messenger={
              error?.response?.data?.message || "Switch workspace failed"
            }
            type="error"
          />
        )
      }
    }

  const renderOrganizationSelected = () => {
    if (!userInfo?.id) {
      return (
        <div className="min-h-[64px] h-[64px] max-h-[64px]  flex items-center justify-center"></div>
      )
    }
    if (organizations.length === 0) {
      return (
        <div className="min-h-[64px] h-[64px] max-h-[64px]  flex items-center justify-center">
          <img
            src={LogoMenu}
            onClick={() => pushTo(PATH.conversations)}
            alt="logo"
            style={{
              width: 30,
              height: 30,
            }}
            className=" rounded cursor-pointer"
          />
        </div>
      )
    }

    const activeOrganization = organizations.find(
      (el) => el.id === organizationSelected.value
    )
    const listStatus = [
      {
        name: "Active",
        status: "ACTIVE",
        color: "#56A012",
      },
      {
        name: "Processing",
        status: "PROCESS",
        color: "#E87A16",
      },
      {
        name: "Blocked",
        status: "INACTIVE",
        color: "#EA4545",
      },
    ]

    return (
      <TippyCustomzie
        containerClass={"component-card-menu"}
        placement={isMobile ? "bottom" : "right-start"}
        interactive
        arrow={false}
        animation="scale"
        visible={openSelectOrganization.value}
        onClickOutside={() => {
          openSelectOrganization.setValue(false)
          organizationSearch.setValue("")
        }}
        allowHTML
        offset={[0, 1]}
        content={
          <div
            style={{
              background: isMobile ? "#ffffff" : "#222222",
              borderRadius: 4,
              paddingTop: 8,
              paddingBottom: 8,
              maxWidth: 330,
              minWidth: 230,
            }}
          >
            <div className="flex items-center justify-between px-[12px] border-b border-[#333333] pb-[8px] pt-[2px]">
              {listStatus.map((item, index) => (
                <div key={index} className="flex items-center gap-[4px]">
                  <div
                    className="rounded-full w-[10px] h-[10px]"
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <Typography
                    className="text-[12px]"
                    style={{
                      color: isMobile ? "#111111" : "#FFFFFF",
                    }}
                  >
                    {item.name}
                  </Typography>
                </div>
              ))}
            </div>
            <div className="px-[12px] pt-[8px] pb-[8px]">
              <div
                className="flex items-center bg-[#333333] rounded-[4px] px-[8px] py-[4px]"
                style={{
                  backgroundColor: isMobile ? "#FFFFFF" : "#333333",
                }}
              >
                <IconSearchNormal
                  style={{
                    width: 16,
                    height: 16,
                    marginRight: 8,
                  }}
                />
                <InputDefault
                  value={organizationSearch.value}
                  onChange={(e) => {
                    organizationSearch.setValue(e.target.value)
                  }}
                  placeholder="Search by code or name"
                  style={{
                    border: "none",
                    boxShadow: "none",
                    background: "transparent",
                    height: 24,
                    fontSize: 12,
                    color: isMobile ? "#111111" : "#FFFFFF",
                    outline: "none",
                  }}
                  classCustom="flex-1 text-[12px] focus:outline-none focus:ring-0"
                />
              </div>
            </div>
            <div
              style={{
                overflowY: "auto",
                maxHeight: 200,
                scrollbarColor: "#333333 transparent",
              }}
            >
              {organizations
                .filter((org) => {
                  if (!organizationSearch.value.trim()) return true
                  const searchLower = organizationSearch.value.toLowerCase()
                  const nameMatch = org.name
                    ?.toLowerCase()
                    .includes(searchLower)
                  const codeMatch = org.code
                    ?.toLowerCase()
                    .includes(searchLower)
                  return nameMatch || codeMatch
                })
                .map((organization, index) => {
                  const status = listStatus.find(
                    (item) => item.status === organization.status
                  )
                  return (
                    <ActionItem
                      key={index}
                      icon={
                        <div
                          className="rounded-full w-[10px] h-[10px]"
                          style={{
                            backgroundColor: Boolean(organization.is_blocked)
                              ? "#EA4545"
                              : status?.color || "#56A012",
                          }}
                        ></div>
                      }
                      title={
                        organization.code
                          ? `${organization.code} - ${organization.name}`
                          : organization.name
                      }
                      onClick={handleSwitchOrganization(
                        organization.id,
                        organization.status,
                        organization.is_blocked,
                        organization.code
                      )}
                      labelColor={isMobile ? "black" : "white"}
                      rootStyle={{
                        backgroundColor:
                          organization.id === organizationSelected.value
                            ? isMobile
                              ? "#F7AC1B"
                              : "#333333"
                            : "transparent",
                      }}
                    />
                  )
                })}
            </div>
            {userInfo?.is_added_organization && !isMobile ? (
              <div className="h-[32px] mt-[8px] pt-[8px] flex items-center justify-center cursor-pointer border-t border-[#333333]">
                <AddButton />
                <Typography
                  className="text-[12px] text-[#F7AC1B]"
                  onClick={() => {
                    pushTo(PATH.organizationAdd)
                    openSelectOrganization.setValue(false)
                  }}
                >
                  Add workspace
                </Typography>
              </div>
            ) : null}
          </div>
        }
      >
        <div
          className="flex items-center justify-center"
          onClick={() => {
            openSelectOrganization.setValue(!openSelectOrganization.value)
          }}
        >
          <div className="min-h-[64px] h-[64px] max-h-[64px]  flex items-center justify-center">
            {activeOrganization?.id ? (
              <OrganizationIconCard
                bg={activeOrganization?.background_color || ""}
                icon={activeOrganization?.logo_url || ""}
                name={activeOrganization?.name || "Personal"}
                zIndex={organizations.length + 10}
                organizationId={activeOrganization?.id || ""}
                width={30}
                height={30}
              />
            ) : (
              <img
                src={LogoMenu}
                onClick={() => pushTo(PATH.conversations)}
                alt="logo"
                style={{
                  width: 30,
                  height: 30,
                }}
                className=" rounded cursor-pointer"
              />
            )}
          </div>
        </div>
      </TippyCustomzie>
    )
  }

  const renderMenuItem = (menu: MenuLeftProps, index: number) => {
    return (
      <Tippy
        key={index}
        className="custom-tippy-menu-left flex-1"
        placement="right"
        content={
          <span
            className="font-normal"
            style={{
              fontSize: 13,
              lineHeight: "22px",
            }}
          >
            {menu?.name}
          </span>
        }
      >
        <div
          onClick={handleMenuPage(menu?.path)}
          className={`h-[60px] md:h-10 w-full flex items-center justify-between  md:justify-center cursor-pointer relative  ${
            index === 10 ? "hover-menu-left-conversation" : "hover-menu-left"
          } ${index + 1 < menuLeft.length && !isMobile ? "mb-3" : ""} ${
            pathnameLocation.includes(menu?.path) ||
            (menu?.path === "/setting" && location.state?.tab === 3) ||
            (pathnameLocation.indexOf("/assign-to") === 0 &&
              menu?.path === PATH.assignment)
              ? "active-menu-left"
              : ""
          }`}
        >
          <div className="flex items-center">
            <div>
              <menu.icon />
            </div>
            <p className="block md:hidden font-semibold md:font-medium pl-2">
              {menu?.name}
            </p>
          </div>
          {menu?.name === "Conversations" && (
            <UnreadConversationIcon
              totalUnread={
                unreadConversation.unread_cc_to_me +
                unreadConversation.unread_from_me +
                unreadConversation.unread_to_me +
                unreadConversation.unread_others
              }
            />
          )}
          {menu?.name === "Builds" && (
            <UnreadIcon
              totalUnread={
                unreadBuild.unread_my_build + unreadBuild.unread_others_build
              }
            />
          )}

          {index === 10 ? null : (
            <div
              className="show-line absolute right-0 h-[60px] md:h-10 w-1 bg-new-yellow"
              style={{
                borderTopLeftRadius: 10,
                borderBottomLeftRadius: 10,
              }}
            />
          )}
        </div>
      </Tippy>
    )
  }

  const renderMainContent = () => (
    <div className="w-auto md:min-w-[72px] flex flex-col bg-new-black-222222 h-full">
      {renderOrganizationSelected()}
      <Clock />

      <div className="flex justify-center flex-col h-[calc(100_*_var(--vh)_-_52px)]">
        {userInfo?.last_organization_id ? (
          <ModalSearch />
        ) : (
          <div className="min-h-[40px]"></div>
        )}

        <div className="flex flex-col flex-1">
          <div className=" flex flex-col justify-start md:justify-center flex-1 pl-6 md:pl-0">
            {menuLeft.map((menu, index) => renderMenuItem(menu, index))}
          </div>

          <div
            className="flex flex-col gap-[12px] bg-[#333333] pt-[12px]"
            style={{
              borderTop: "1px solid #444444",
              alignItems: isMobile ? "flex-start" : "center",
            }}
          >
            <div
              style={{
                marginLeft: isMobile ? 24 : 0,
              }}
            >
              {userInfo?.id ? (
                renderMenuItem(MENU_LEFT_CONVERSATIONS, 10)
              ) : (
                <div className="h-[40px]"></div>
              )}
            </div>
            <TippyCustomzie
              containerClass={"menu-bar-avatar component-card-menu"}
              placement="bottom"
              interactive
              arrow={false}
              animation="scale"
              visible={showMenuTippy.value}
              onClickOutside={() => showMenuTippy.setValue(false)}
              offset={[0, 5]}
              allowHTML
              content={
                <div
                  style={{
                    background: isMobile ? "#ffffff" : "#222222",
                    borderRadius: 4,
                    paddingTop: 8,
                    paddingBottom: 8,
                    width: 120,
                  }}
                >
                  <ActionItem
                    title="My profile"
                    onClick={onClickAction("myProfile")}
                    labelColor={isMobile ? "black" : "white"}
                  />
                  <ActionItem
                    title="Settings"
                    onClick={() => {
                      pushTo("/generals", undefined, { tab: 3 })
                    }}
                    labelColor={isMobile ? "black" : "white"}
                  />
                  <ActionItem
                    title="Logout"
                    onClick={onClickAction("logout")}
                    labelColor={isMobile ? "black" : "white"}
                  />
                </div>
              }
            >
              <div
                className="flex flex-col items-center pb-[18px] md:pb-0"
                style={{
                  marginLeft: isMobile ? 24 : 0,
                }}
              >
                <div
                  className="cursor-pointer flex items-center "
                  onClick={handleClickTippy}
                >
                  <Avatar
                    name={userInfo.fullname ?? ""}
                    avatar={userInfo.avatar ?? ""}
                    width="30px"
                    height="30px"
                  />
                  {userInfo.fullname ? (
                    <Typography className="block md:hidden font-semibold md:font-medium pl-2">
                      {userInfo.fullname}
                    </Typography>
                  ) : null}
                </div>

                <p className="self-start md:self-center md:ml-0 md:text-center font-medium text-[11px] text-[#C7C7C7] mt-[12px]">
                  <Tippy
                    key="tippy-logs"
                    offset={[0, 27]}
                    className="custom-tippy-menu-left"
                    placement="left"
                    content={<p>View update logs</p>}
                  >
                    <a
                      href={RELEASE_NOTE_URL}
                      target="_blank"
                      rel="noreferrer"
                      className=" hover:underline"
                    >
                      {`${REACT_APP_API_VERSION} Beta`}
                    </a>
                  </Tippy>
                </p>
                {fontEndVersion ? (
                  <div
                    className="flex flex-col w-full  items-start md:items-center mb-[50px] md:mb-[12px] mt-[8px]"
                    style={{
                      flexDirection: isMobile ? "row" : "column",
                      gap: isMobile ? 8 : 0,
                    }}
                  >
                    <p className=" text-[10px] text-[#7A7A7A]">Build</p>
                    <p className=" text-[10px] text-[#7A7A7A]">
                      {fontEndVersion}
                    </p>
                  </div>
                ) : null}
              </div>
            </TippyCustomzie>
          </div>
        </div>
      </div>

      {showLogout.value ? (
        <LogoutCard handleChangeButton={handleChangeButton} />
      ) : null}
    </div>
  )

  return isMobile ? (
    <div className="bg-new-black-222222">
      <TippyLayout
        type="content-screen"
        visible={showMenuBar.value}
        mainContent={renderMainContent()}
      >
        <div className="flex items-center justify-between min-h-[52px] px-6">
          <div className="w-full flex ">
            <TraceliumLogo onClick={() => pushTo(PATH.conversations)} />
          </div>

          <div
            className="cursor-pointer h-[52px] w-[52px] flex items-center justify-end"
            onClick={() => {
              showMenuBar.setValue(!showMenuBar.value)
            }}
          >
            {showMenuBar.value ? <CloseIcon /> : <MenuBar />}
          </div>
        </div>
      </TippyLayout>
    </div>
  ) : (
    renderMainContent()
  )
}
export default MenuLeft
